import { create } from 'zustand';
import { getSocket } from '@/lib/socket';
import { CallPeer, MediaPermissionError, getLocalStream } from '@/lib/call/peer';
import { callsApi, type Call, type CallMode, type IceServer } from '@/api/calls';

// ── Canonical v1 call signaling contract ─────────────────────────────────────
//
// SDP format:   always raw SDP text (e.g. "v=0\r\n..."). No JSON wrapper.
// ICE format:   always RTCIceCandidateInit object { candidate, sdpMid, sdpMLineIndex }.
// Server relay: no fromUserId — peer identity is known from the Call row.

export type CallPhase =
  | 'idle'
  | 'outgoing'        // we initiated; waiting for callee to pick up
  | 'incoming'        // we received an invite; ring UI is on screen
  | 'connecting'      // accepted; negotiating SDP/ICE
  | 'active'          // media flowing
  | 'ended';          // terminal — UI stays briefly then clears

export interface CallUIState {
  call: Call | null;
  phase: CallPhase;
  mode: CallMode;
  error: string | null;
  micEnabled: boolean;
  cameraEnabled: boolean;
  localStream: MediaStream | null;
  remoteStream: MediaStream | null;
  peer: CallPeer | null;
  iceServers: IceServer[] | null;
  pendingPeerName: string | null;

  // ── Actions ──────────────────────────────────────────────────────────────
  startCall: (calleeId: string, mode: CallMode, calleeName?: string) => Promise<void>;
  handleIncoming: (call: Call) => void;
  acceptIncoming: () => Promise<void>;
  rejectIncoming: () => void;
  cancelOutgoing: () => void;
  hangup: () => void;
  toggleMic: () => void;
  toggleCamera: () => void;
  onCallState: (call: Call) => Promise<void>;
  onOffer: (callId: string, sdp: string) => Promise<void>;
  onAnswer: (callId: string, sdp: string) => Promise<void>;
  onIceCandidate: (callId: string, candidate: unknown) => Promise<void>;
  onError: (message: string) => void;
  reset: () => void;
}

function initialState() {
  return {
    call: null,
    phase: 'idle' as CallPhase,
    mode: 'VOICE' as CallMode,
    error: null,
    micEnabled: true,
    cameraEnabled: true,
    localStream: null,
    remoteStream: null,
    peer: null,
    iceServers: null as IceServer[] | null,
    pendingPeerName: null as string | null,
  };
}

function isTerminal(status: Call['status']): boolean {
  return (
    status === 'ENDED' ||
    status === 'REJECTED' ||
    status === 'CANCELLED' ||
    status === 'MISSED' ||
    status === 'FAILED'
  );
}

async function ensureIceServers(get: () => CallUIState, set: (partial: Partial<CallUIState>) => void): Promise<IceServer[]> {
  const existing = get().iceServers;
  if (existing) return existing;
  const iceServers = await callsApi.getIceServers();
  set({ iceServers });
  return iceServers;
}

function teardownPeer(state: CallUIState) {
  if (state.peer) {
    try { state.peer.close(); } catch { /* ignore */ }
  }
  if (state.localStream) {
    for (const t of state.localStream.getTracks()) {
      try { t.stop(); } catch { /* ignore */ }
    }
  }
}

/**
 * Create a CallPeer wired to the socket for a given call.
 */
function createWiredPeer(
  iceServers: IceServer[],
  callId: string,
  set: (partial: Partial<CallUIState>) => void,
  get: () => CallUIState,
): CallPeer {
  return new CallPeer({
    iceServers,
    onIceCandidate: (candidate) => {
      // Canonical format: RTCIceCandidateInit object
      getSocket().emit('call:ice-candidate', { callId, candidate });
    },
    onTrack: (remoteStream) => set({ remoteStream }),
    onStateChange: (pcState) => {
      if (pcState === 'connected') set({ phase: 'active' });
      if (pcState === 'failed' || pcState === 'closed') {
        const c = get().call;
        if (c && !isTerminal(c.status)) {
          getSocket().emit('call:hangup', { callId: c.id, reason: 'media_error' });
        }
      }
    },
  });
}

export const useCallStore = create<CallUIState>((set, get) => ({
  ...initialState(),

  // ── Outgoing ───────────────────────────────────────────────────────────────
  startCall: async (calleeId, mode, calleeName) => {
    if (get().phase !== 'idle') return;
    set({ error: null, phase: 'outgoing', mode, pendingPeerName: calleeName ?? null });

    let stream: MediaStream;
    try {
      stream = await getLocalStream(mode);
    } catch (err) {
      const code = err instanceof MediaPermissionError ? err.kind : 'other';
      const message =
        code === 'denied'      ? 'Нет доступа к микрофону/камере' :
        code === 'no-device'   ? 'Устройство ввода не найдено' :
        code === 'unsupported' ? 'Браузер не поддерживает WebRTC' :
        'Не удалось получить локальный поток';
      set({ error: message, phase: 'ended' });
      setTimeout(() => get().reset(), 1500);
      return;
    }
    set({ localStream: stream });

    const socket = getSocket();
    socket.emit('call:start', { calleeId, mode });
  },

  // ── Incoming ───────────────────────────────────────────────────────────────
  handleIncoming: (call) => {
    if (get().phase !== 'idle') {
      getSocket().emit('call:reject', { callId: call.id });
      return;
    }
    set({
      call,
      phase: 'incoming',
      mode: call.mode,
      error: null,
      micEnabled: true,
      cameraEnabled: true,
    });
  },

  acceptIncoming: async () => {
    const state = get();
    if (state.phase !== 'incoming' || !state.call) return;

    let stream: MediaStream;
    try {
      stream = await getLocalStream(state.mode);
    } catch (err) {
      const code = err instanceof MediaPermissionError ? err.kind : 'other';
      const message =
        code === 'denied'      ? 'Нет доступа к микрофону/камере' :
        code === 'no-device'   ? 'Устройство ввода не найдено' :
        code === 'unsupported' ? 'Браузер не поддерживает WebRTC' :
        'Не удалось получить локальный поток';
      set({ error: message });
      getSocket().emit('call:reject', { callId: state.call.id });
      set({ phase: 'ended' });
      setTimeout(() => get().reset(), 1500);
      return;
    }

    const iceServers = await ensureIceServers(get, set);
    const peer = createWiredPeer(iceServers, state.call.id, set, get);
    peer.attachLocalStream(stream);

    set({
      localStream: stream,
      peer,
      phase: 'connecting',
    });
    getSocket().emit('call:accept', { callId: state.call.id });
  },

  rejectIncoming: () => {
    const state = get();
    if (state.phase !== 'incoming' || !state.call) return;
    getSocket().emit('call:reject', { callId: state.call.id });
    set({ phase: 'ended' });
    setTimeout(() => get().reset(), 800);
  },

  cancelOutgoing: () => {
    const state = get();
    if (!state.call) {
      teardownPeer(state);
      get().reset();
      return;
    }
    getSocket().emit('call:cancel', { callId: state.call.id });
    set({ phase: 'ended' });
    setTimeout(() => get().reset(), 800);
  },

  hangup: () => {
    const state = get();
    if (!state.call) {
      teardownPeer(state);
      get().reset();
      return;
    }
    getSocket().emit('call:hangup', { callId: state.call.id });
    setTimeout(() => {
      const s = get();
      if (s.phase !== 'idle' && s.call?.id === state.call?.id) {
        teardownPeer(s);
        get().reset();
      }
    }, 2000);
  },

  toggleMic: () => {
    const { peer, micEnabled } = get();
    if (!peer) return;
    const next = !micEnabled;
    peer.setMicEnabled(next);
    set({ micEnabled: next });
  },

  toggleCamera: () => {
    const { peer, cameraEnabled } = get();
    if (!peer) return;
    const next = !cameraEnabled;
    peer.setCameraEnabled(next);
    set({ cameraEnabled: next });
  },

  // ── Socket event plumbing ─────────────────────────────────────────────────
  onCallState: async (call) => {
    const prev = get();
    set({ call });

    if (isTerminal(call.status)) {
      teardownPeer(prev);
      set({ phase: 'ended' });
      setTimeout(() => {
        if (get().call?.id === call.id) get().reset();
      }, 1500);
      return;
    }

    if (call.status === 'RINGING' && prev.phase === 'outgoing') {
      return;
    }

    // Callee accepted → caller creates the SDP offer.
    if (call.status === 'ACCEPTED' && prev.phase === 'outgoing') {
      try {
        const iceServers = await ensureIceServers(get, set);
        const peer = createWiredPeer(iceServers, call.id, set, get);
        if (prev.localStream) peer.attachLocalStream(prev.localStream);
        set({ peer, phase: 'connecting' });

        const offer = await peer.createOffer();
        // Canonical: send raw SDP text, no JSON.stringify wrapper
        getSocket().emit('call:offer', { callId: call.id, sdp: offer.sdp! });
      } catch (err) {
        console.error('call: failed to create offer', err);
        getSocket().emit('call:hangup', { callId: call.id, reason: 'media_error' });
      }
    }
  },

  onOffer: async (callId, sdp) => {
    const state = get();
    if (!state.peer || !state.call || state.call.id !== callId) return;
    try {
      // Canonical: sdp is always raw text. Type is implicit = 'offer'.
      const answer = await state.peer.acceptOfferAndAnswer({ type: 'offer', sdp });
      // Canonical: send raw SDP text
      getSocket().emit('call:answer', { callId, sdp: answer.sdp! });
    } catch (err) {
      console.error('call: handling offer failed', err);
      getSocket().emit('call:hangup', { callId, reason: 'media_error' });
    }
  },

  onAnswer: async (callId, sdp) => {
    const state = get();
    if (!state.peer || !state.call || state.call.id !== callId) return;
    try {
      // Canonical: sdp is always raw text. Type is implicit = 'answer'.
      await state.peer.acceptAnswer({ type: 'answer', sdp });
    } catch (err) {
      console.error('call: handling answer failed', err);
      getSocket().emit('call:hangup', { callId, reason: 'media_error' });
    }
  },

  onIceCandidate: async (callId, candidate) => {
    const state = get();
    if (!state.peer || !state.call || state.call.id !== callId) return;
    try {
      // Canonical: candidate is always { candidate, sdpMid, sdpMLineIndex }
      // Backend normalizes before relay, so we can trust the shape.
      const init = candidate as RTCIceCandidateInit;
      await state.peer.addRemoteCandidate(init);
    } catch {
      /* ignore — Chrome sometimes throws on duplicate / null candidates */
    }
  },

  onError: (message) => {
    set({ error: message });
    const state = get();
    teardownPeer(state);
    set({ phase: 'ended' });
    setTimeout(() => {
      if (get().phase === 'ended') get().reset();
    }, 1500);
  },

  reset: () => {
    const { iceServers } = get();
    set({ ...initialState(), iceServers });
  },
}));
